import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  KltModalService,
  TableColumnGroup,
  TableColumns,
  TableData,
} from '@kolytics/shared-components';
import { KltToastService } from 'libs/shared-components/src/lib/services/toast/toast.service';
import {
  IAssumptionProfileScenarioResponse,
  IReitData,
} from '../../../../interfaces';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ReitStoreService } from '../../../../services/reit-store.service';
import { AssumptionBaseComponent } from '../../../../utils/components/assumption/assumption-base-component';
import { TableV2Component } from '../../../../../../../../../../libs/shared-components/src/lib/components/table-v2/table-v2.component';
import {
  ReitAssumptionService,
  ReitService,
  UserAssumptionsService,
} from '@kolytics/shared-api';

@Component({
  selector: 'klt-other-erv-table',
  templateUrl: './other-erv-table.component.html',
  styleUrls: ['./other-erv-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TableV2Component],
})
export class OtherErvTableComponent
  extends AssumptionBaseComponent
  implements OnInit
{
  public reitTableData: IReitData = {} as IReitData;
  @Input()
  set ervGrowthPeriod(value: IAssumptionProfileScenarioResponse | null) {
    if (value) {
      this.bindingVrvGrowthPeriod(value);
    }
  }
  @Input()
  reitId!: number;
  @Input()
  userScenarioId!: number;

  @Output() cellSaved = new EventEmitter<any>();

  colsDefName = {
    userGrowthName: 'growth_user',
    userWeightingName: 'weighting_user',
    baseGrowthName: 'growth_base',
    baseWeightingName: 'weighting_base',
  };

  constructor(
    protected readonly kltModalService: KltModalService,
    protected readonly kltToastService: KltToastService,
    protected readonly router: Router,
    protected reitService: ReitService,
    protected spinner: NgxSpinnerService,
    protected reitStoreService: ReitStoreService,
    protected cdr: ChangeDetectorRef,
    protected reitAssumptionService: ReitAssumptionService,
    protected userAssumptionService: UserAssumptionsService,
  ) {
    super(
      kltModalService,
      kltToastService,
      router,
      reitService,
      userAssumptionService,
      spinner,
      reitStoreService,
      cdr,
      reitAssumptionService,
    );
  }

  public ngOnInit(): void {
    super.ngOnInit();
  }

  protected valueOfReitId(): string {
    return this.reitId.toString();
  }

  protected valueOfUserScenarioId(): number | string {
    return this.userScenarioId;
  }

  bindingVrvGrowthPeriod(ervGrowthPeriod: IAssumptionProfileScenarioResponse) {
    const { sections, header } = this.createSectionsAndHeaders(ervGrowthPeriod);
    const stickLeftColumns: TableColumns = {
      growth_user: {
        title: 'ERV growth period',
        fill:
          (ervGrowthPeriod.result.userAssumptionOther?.weightingData.length ||
            ervGrowthPeriod.result.assumptionOther?.weightingData.length) > 0
            ? 'purple'
            : 'dark',
        sortable: false,
        type: 'text',
      },
    };
    this.reitTableData = {
      hideSection: true,
      sections: sections,
      headers: header,
      data: this.createScenarioData(ervGrowthPeriod),
      total: 0,
      stickLeftColumns: stickLeftColumns,
    };
  }

  onCellSave({
    value,
    row,
    column,
  }: {
    value: string;
    row: any;
    column: number;
  }): void {
    this.cellSaved.emit({
      value,
      row,
      column,
      reitTableData: this.reitTableData,
      methodName: `Get${row['growth_user'].data}`,
    });
  }

  createSectionsAndHeaders(data: IAssumptionProfileScenarioResponse) {
    const userAssumptionsOther =
      data.result.userAssumptionOther?.weightingData ||
      data.result.assumptionOther?.weightingData;
    const baseAssumptionsOther =
      data.result.baseAssumptionOther?.weightingData ||
      data.result.userBaseAssumptionOther.weightingData;
    const sections: TableColumnGroup[] = [
      { sectionTitle: '', columnDefs: ['baseGrowthName'], sticky: true },
    ];
    const header: TableColumns = {};
    if (userAssumptionsOther.length > 0) {
      sections.push({
        sectionTitle: data.result.userScenario.name,
        columnDefs: [this.colsDefName.userWeightingName],
        spacing: true,
        spacingFill: 'light',
      });
      header[this.colsDefName.userGrowthName] = {
        title: 'ERV growth period',
        fill: 'purple',
        sortable: true,
        type: 'text',
        sticky: true,
        style: { center: true },
      };
      header[this.colsDefName.userWeightingName] = {
        title: 'Weighting',
        fill: 'purple',
        sortable: true,
        type: 'text',
        style: { center: true },
      };
    }

    if (baseAssumptionsOther.length > 0) {
      sections.push({
        sectionTitle: data.result.userBaseScenario?.name || 'Base',
        columnDefs: [this.colsDefName.baseWeightingName],
      });
      header[this.colsDefName.baseWeightingName] = {
        title: 'Weighting',
        fill: 'forecast',
        sortable: true,
        type: 'text',
        colspan: 1,
        style: { center: true },
      };
    }

    return { sections, header };
  }

  createScenarioData(data: IAssumptionProfileScenarioResponse): TableData[] {
    const userAssumptionsOther =
      data.result.userAssumptionOther?.weightingData ||
      data.result.assumptionOther?.weightingData;
    const baseAssumptionsOther =
      data.result.baseAssumptionOther?.weightingData ||
      data.result.userBaseAssumptionOther.weightingData;
    if (baseAssumptionsOther.length > 0) {
      return baseAssumptionsOther.map((item) => {
        const tableItem: TableData = {};
        if (userAssumptionsOther.length > 0) {
          let userItem = userAssumptionsOther.find(
            (x) => x.ervGrowthPeriod === item.ervGrowthPeriod,
          );
          if (!userItem) {
            userItem = { ...item, value: 0 };
          }
          tableItem[this.colsDefName.userGrowthName] = {
            data: userItem.ervGrowthPeriod,
            fill: 'none',
          };
          tableItem[this.colsDefName.userWeightingName] = {
            data:
              userItem.type === 'percentage'
                ? (userItem.value * 100).toFixed(2)
                : userItem.value.toFixed(2),
            editable: userItem.editable,
            type: userItem.type,
            rowId: userItem.rowId,
            style: { center: true },
          };
        }
        tableItem[this.colsDefName.baseGrowthName] = {
          data: item.ervGrowthPeriod,
        };
        tableItem[this.colsDefName.baseWeightingName] = {
          data:
            item.type === 'percentage'
              ? (item.value * 100).toFixed(2)
              : item.value.toFixed(2),
          editable: item.editable,
          style: { center: true },
        };

        return tableItem;
      });
    }
    return [];
  }
}
